import {
  Box,
  Heading,
  Button,
  Text,
  Badge,
  Flex,
  Link as ChakraLink,
  Image,
} from "@chakra-ui/react";
import { FiEye, FiList, FiLink, FiMail } from "react-icons/fi";
import { setStatusColor } from "../../util";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";

interface MirrorCardProps {
  project: CommunityMirrorProject;
}

function MirrorCard({ project }: MirrorCardProps) {
  const navigate = useNavigate();
  // Handle both hostedUrl and hostedProjectUrl for backward compatibility
  const hostUrl = project.hostedUrl || project.hostedProjectUrl;

  // Use the first custom domain if available
  const customDomain =
    project.customProjectUrls && project.customProjectUrls.length > 0
      ? project.customProjectUrls[0].url
      : null;

  // URL to use for the View site button
  const viewSiteUrl = customDomain || hostUrl;

  return (
    <Box
      key={project.id}
      borderWidth="1px"
      borderRadius="lg"
      overflow="hidden"
      py={{ base: 3, md: 4 }}
      px={{ base: 4, md: 6 }}
      boxShadow="sm"
      background="white"
      _hover={{
        boxShadow: "md",
        textDecoration: "none",
      }}
      position="relative"
      display="flex"
      flexDirection="column"
      minHeight="200px"
    >
      <Flex direction="column" gap={{ base: 3, md: 5 }} flexGrow={1}>
        <Box>
          <Flex
            alignItems={{ base: "start", sm: "center" }}
            justify="space-between"
            gap={2}
            flexDir={{ base: "column", sm: "row" }}
          >
            <Flex alignItems="center" gap={2}>
              <Heading fontWeight="bold" fontSize={{ base: 16, md: 18 }}>
                {project.name}
              </Heading>

              {project.runByEarthFast && (
                <Flex
                  alignItems="center"
                  bg="rgba(245, 250, 255, 0.95)"
                  color="#0052CC"
                  px={1.5}
                  py={0.5}
                  rounded="md"
                  fontSize="xs"
                  fontWeight={600}
                  ml={2}
                  boxShadow="0 0 0 1px rgba(0, 82, 204, 0.4)"
                >
                  <Image
                    src="/earthfast-logo.svg"
                    height="14px"
                    mr={1.5}
                    display="inline"
                  />
                  <Text>Run by EarthFast</Text>
                </Flex>
              )}
            </Flex>

            {/* Badge for project status (external or build status) */}
            {project.isExternal ? (
              <Badge
                bg="blue.50"
                color="blue.600"
                textTransform="capitalize"
                px={2}
                py={{ base: 0.5, md: 1 }}
                rounded="400px"
                fontWeight={500}
                fontSize="xs"
                borderColor="blue.300"
                borderWidth="1px"
                whiteSpace="nowrap"
              >
                <Flex alignItems="center" gap={1}>
                  <Flex
                    width="6px"
                    height="6px"
                    borderRadius="50%"
                    bg="blue.600"
                  />
                  External
                </Flex>
              </Badge>
            ) : (
              project.lastBuildStatus && (
                <Badge
                  bg={`${setStatusColor(project.lastBuildStatus)}.50`}
                  color={`${setStatusColor(project.lastBuildStatus)}.600`}
                  textTransform="capitalize"
                  px={2}
                  py={{ base: 0.5, md: 1 }}
                  rounded="400px"
                  fontWeight={500}
                  fontSize="xs"
                  borderColor={`${setStatusColor(project.lastBuildStatus)}.300`}
                  borderWidth="1px"
                  whiteSpace="nowrap"
                >
                  <Flex alignItems="center" gap={1}>
                    <Flex
                      width="6px"
                      height="6px"
                      borderRadius="50%"
                      bg={`${setStatusColor(project.lastBuildStatus)}.600`}
                    />
                    {project.lastBuildStatus === "in-progress"
                      ? "Building"
                      : project.lastBuildStatus}
                  </Flex>
                </Badge>
              )
            )}
          </Flex>

          {/* Display custom domain if available */}
          {viewSiteUrl && (
            <Flex
              alignItems="center"
              gap={1}
              justifyContent="left"
              my={{ base: 2, md: 1 }}
              maxW="100%"
            >
              <Box w="16px" h="16px" flexShrink={0}>
                <FiLink size={14} color="#637381" />
              </Box>
              <ChakraLink
                href={`https://${viewSiteUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                display="inline-block"
                width="100%"
                onClick={(
                  e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
                ) => {
                  e.stopPropagation();
                  window.open(`https://${viewSiteUrl}`, "_blank");
                }}
              >
                <Text fontSize={{ base: "xs", md: "sm" }} wordBreak="break-all">
                  {`https://${viewSiteUrl}`}
                </Text>
              </ChakraLink>
            </Flex>
          )}
          {project.email && (
            <Flex
              alignItems="center"
              gap={1}
              justifyContent="left"
              my={{ base: 2, md: 1 }}
              maxW="100%"
            >
              <Box w="16px" h="16px" flexShrink={0}>
                <FiMail size={14} color="#637381" />
              </Box>
              <ChakraLink
                href={`mailto:${project.email}`}
                display="inline-block"
                width="100%"
                onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) =>
                  e.stopPropagation()
                }
              >
                <Text fontSize={{ base: "xs", md: "sm" }} wordBreak="break-all">
                  {project.email}
                </Text>
              </ChakraLink>
            </Flex>
          )}

          {!project.isExternal && (
            <Flex
              alignItems={{ base: "start", sm: "center" }}
              gap={2}
              mt={1}
              flexDir={{ base: "column", sm: "row" }}
            >
              <Badge
                bg="purple.50"
                color="purple.700"
                px={2}
                py={0.5}
                rounded="md"
                fontSize="xs"
                mt={1}
              >
                {project.totalBuilds} BUILDS
              </Badge>
              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500" mt={1}>
                <Text as="span" fontWeight="semibold">
                  Last build
                </Text>{" "}
                {project.lastBuildTime
                  ? formatDistanceToNow(new Date(project.lastBuildTime), {
                      addSuffix: true,
                    })
                  : "No builds yet"}
              </Text>
            </Flex>
          )}
        </Box>
      </Flex>

      {/* Bottom buttons section - always at the bottom */}
      <Flex gap={2} flexWrap="wrap" mt="auto" pt={3}>
        {viewSiteUrl && (
          <Button
            as="a"
            leftIcon={<FiEye size={14} />}
            colorScheme="gray"
            href={`https://${viewSiteUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            variant="outline"
            size="sm"
            fontSize={{ base: "xs", md: "sm" }}
            h={{ base: "30px", md: "32px" }}
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              e.preventDefault();
              e.stopPropagation();
              window.open(`https://${viewSiteUrl}`, "_blank");
            }}
          >
            View site
          </Button>
        )}

        {!project.isExternal && (
          <Button
            leftIcon={<FiList size={14} />}
            colorScheme="gray"
            variant="outline"
            size="sm"
            fontSize={{ base: "xs", md: "sm" }}
            h={{ base: "30px", md: "32px" }}
            onClick={() => navigate(`/project-showcase/${project.id}`)}
          >
            Pipeline
          </Button>
        )}
      </Flex>
    </Box>
  );
}

export default MirrorCard;
